import { useState } from "react";
import { styled, keyframes } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { SidePanelSection } from "../../ui";
import { ChevronRight, TrendDown, TrendUp } from "@acto/web-common/components";
import { useEmployeeClients } from "@acto/taskpane/core/data-access/team/useEmployeeClients";
import { DateRangeInput } from "@acto/gql/graphql";
import { formatCurrency, formatProfitWithMargin } from "../../../../utils/formatters";
import { useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { LoadMoreButton } from "../../../DataTable/ui/LoadMoreButton";

type MetricTab = "revenue" | "profit";

interface CustomersSectionProps {
  employeeId: string;
  dateRange: DateRangeInput;
  locale: string;
  onCustomerClick?: (id: string, name: string) => void;
  showTrends?: boolean;
}

const TabContainer = styled("div")(({ theme }) => ({
  display: "flex",
  gap: 0,
  backgroundColor: theme.managementView.colors.neutral[0],
  borderRadius: theme.managementView.radius[32],
  padding: theme.managementView.spacing[4],
  border: `1px solid ${theme.managementView.colors.neutral[8]}`,
}));

const TabBase = styled("button")(({ theme }) => ({
  flex: 1,
  padding: `${theme.managementView.spacing[10]} ${theme.managementView.spacing[16]}`,
  border: "none",
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[500],
  cursor: "pointer",
  transition: "all 0.2s",
  borderRadius: theme.managementView.radius[32],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
}));

const TabActive = styled(TabBase)(({ theme }) => ({
  backgroundColor: theme.managementView.colors.neutral[4],
  color: theme.managementView.colors.neutral[90],
  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
}));

const TabInactive = styled(TabBase)(({ theme }) => ({
  backgroundColor: "transparent",
  color: theme.managementView.colors.neutral[56],

  "&:hover": {
    color: theme.managementView.colors.neutral[90],
  },
}));

const CustomerList = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[8],
}));

const CustomerItem = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: `${theme.managementView.spacing[16]} ${theme.managementView.spacing[18]}`,
  marginLeft: `-${theme.managementView.spacing[18]}`,
  width: `calc(100% + ${theme.managementView.spacing[18]})`,
  backgroundColor: theme.managementView.colors.neutral[0],
  border: "none",
  borderRadius: theme.managementView.radius[16],
  textAlign: "left",

  "& svg": {
    width: "10px",
    height: "10px",
    color: theme.managementView.colors.neutral[64],
  },

  "&[data-clickable='true']": {
    cursor: "pointer",
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: theme.managementView.colors.neutral[4],
    },
  },
}));

const CustomerContent = styled("div")({
  display: "flex",
  alignItems: "start",
  gap: "16px",
  flex: 1,
});

const ClientRow = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  columnGap: theme.managementView.spacing[16],
  flex: 1,

  "&[data-has-trend='true']": {
    gridTemplateColumns: "auto 1fr",
  },
}));

const ClientHeading = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[4],
  gridColumn: "-2 / -1",
  gridRow: 1,
  paddingTop: theme.managementView.spacing[4],
}));

const TrendIconContainer = styled("div")(({ theme }) => ({
  gridColumn: 1,
  gridRow: 1,
  alignSelf: "start",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "44px",
  height: "28px",
  borderRadius: theme.managementView.radius[32],
  flexShrink: 0,
}));

const TrendIconUpContainer = styled(TrendIconContainer)(({ theme }) => ({
  backgroundColor: theme.managementView.colors.success[4],

  "& svg": {
    width: "12px",
    height: "12px",
    color: theme.managementView.colors.success[100],
  },
}));

const TrendIconDownContainer = styled(TrendIconContainer)(({ theme }) => ({
  backgroundColor: theme.managementView.colors.error[4],

  "& svg": {
    width: "12px",
    height: "12px",
    color: theme.managementView.colors.error[100],
  },
}));

const CustomerInfo = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  flex: 1,
});

const CustomerName = styled("p")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[440],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[90],
  margin: 0,
}));

const CustomerExternalReference = styled("p")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[400],
  color: theme.managementView.colors.neutral[56],
  margin: 0,
}));

const CustomerRevenue = styled("p")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[56],
  margin: 0,
}));

const ClientValue = styled(CustomerRevenue)(({ theme }) => ({
  gridColumn: "-2 / -1",
  gridRow: 2,
  marginTop: theme.managementView.spacing[8],
}));

const shimmer = keyframes({
  "0%": { backgroundPosition: "-200px 0" },
  "100%": { backgroundPosition: "200px 0" },
});

const SkeletonElement = styled("div")(({ theme }) => ({
  borderRadius: theme.managementView.radius[10],
  background: `linear-gradient(90deg, ${theme.colors.neutral[200]} 0%, ${theme.colors.neutral[100]} 50%, ${theme.colors.neutral[200]} 100%)`,
  backgroundSize: "400px 100%",
  animation: `${shimmer} 1.5s ease-in-out infinite`,
}));

const SkeletonIcon = styled(SkeletonElement)(({ theme }) => ({
  width: "44px",
  height: "28px",
  borderRadius: theme.managementView.radius[32],
  flexShrink: 0,
}));

const SkeletonName = styled(SkeletonElement)({
  height: "15px",
  width: "120px",
});

const SkeletonValue = styled(SkeletonElement)({
  height: "14px",
  width: "80px",
});

const formatChange = (change: number | null): string | null => {
  if (change === null) return null;
  return `${change > 0 ? "+" : ""}${Math.round(change)}%`;
};

export const CustomersSection = ({
  employeeId,
  dateRange,
  locale,
  onCustomerClick,
  showTrends = true,
}: CustomersSectionProps) => {
  const t = useTranslations();
  const currency = useCurrencyContext();
  const [activeTab, setActiveTab] = useState<MetricTab>("revenue");
  const isClickable = Boolean(onCustomerClick);

  const { clients, isPending, isFetching, hasNextPage, fetchNextPage } = useEmployeeClients({
    employeeId,
    dateRange,
  });

  const RevenueTab = activeTab === "revenue" ? TabActive : TabInactive;
  const ProfitTab = activeTab === "profit" ? TabActive : TabInactive;

  return (
    <SidePanelSection title={t("managementView.employee.customers")}>
      <TabContainer>
        <RevenueTab onClick={() => setActiveTab("revenue")}>{t("managementView.employee.revenueTab")}</RevenueTab>
        <ProfitTab onClick={() => setActiveTab("profit")}>{t("managementView.employee.margin")}</ProfitTab>
      </TabContainer>
      <CustomerList>
        {isPending ? (
          Array.from({ length: 5 }).map((_, index) => (
            <CustomerItem key={index} as="div" style={{ cursor: "default" }}>
              <CustomerContent>
                <SkeletonIcon />
                <CustomerInfo>
                  <SkeletonName />
                  <SkeletonValue />
                </CustomerInfo>
              </CustomerContent>
            </CustomerItem>
          ))
        ) : clients.length === 0 ? (
          <CustomerItem as="div" style={{ cursor: "default" }}>
            <CustomerContent>
              <CustomerInfo>
                <CustomerName>{t("managementView.employee.noCustomers")}</CustomerName>
              </CustomerInfo>
            </CustomerContent>
          </CustomerItem>
        ) : (
          clients.map((client) => {
            const displayValue =
              activeTab === "revenue"
                ? formatCurrency(client.revenue, locale, currency)
                : formatProfitWithMargin(client.profit, client.margin, locale, currency);
            const change = !showTrends ? null : activeTab === "revenue" ? client.revenueChange : client.profitChange;
            const changeText = formatChange(change);

            return (
              <CustomerItem
                key={client.id}
                data-clickable={isClickable}
                onClick={onCustomerClick ? () => onCustomerClick(client.id, client.name) : undefined}
              >
                <ClientRow data-has-trend={change !== null}>
                  {change !== null &&
                    (change >= 0 ? (
                      <TrendIconUpContainer>
                        <TrendUp />
                      </TrendIconUpContainer>
                    ) : (
                      <TrendIconDownContainer>
                        <TrendDown />
                      </TrendIconDownContainer>
                    ))}
                  <ClientHeading>
                    <CustomerName>{client.name}</CustomerName>
                    {client.externalReference && (
                      <CustomerExternalReference>#{client.externalReference}</CustomerExternalReference>
                    )}
                  </ClientHeading>
                  <ClientValue>
                    {displayValue}
                    {changeText && ` · ${changeText}`}
                  </ClientValue>
                </ClientRow>
                {isClickable && <ChevronRight />}
              </CustomerItem>
            );
          })
        )}
      </CustomerList>
      {clients.length > 0 && (
        <LoadMoreButton
          onClick={fetchNextPage}
          label={t("common.loadMore")}
          isLoading={isFetching}
          disabled={!hasNextPage}
        />
      )}
    </SidePanelSection>
  );
};
